// A string-keyed record kept in one localStorage slot, parsed only when the
// stored string changes: claims and structures are both read per drawn tile or
// per frame, which a JSON.parse on every read cannot afford.

export interface StoredRecord<T> {
  /** The record as stored now; the same object until the stored string moves. */
  load(): Record<string, T>;
  save(next: Record<string, T>): void;
  /** Forget the cached string, so the next `load()` parses again. */
  invalidate(): void;
}

/** `onChange` runs whenever the record may have moved: a parse of a new string, or a save. */
export function createStoredRecord<T>(key: string, onChange?: () => void): StoredRecord<T> {
  let rawCache: string | null = null;
  let cache: Record<string, T> = {};
  return {
    load() {
      let raw: string;
      try {
        raw = localStorage.getItem(key) ?? '{}';
      } catch {
        raw = '{}';
      }
      if (raw !== rawCache) {
        rawCache = raw;
        try {
          const parsed: unknown = JSON.parse(raw);
          // A bad value (null, an array, a string) parses fine and is not a
          // record; every reader assumes Object.values/keys on it.
          cache = parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as Record<string, T>
            : {};
        } catch {
          cache = {};
        }
        onChange?.();
      }
      return cache;
    },
    save(next) {
      const raw = JSON.stringify(next);
      try {
        localStorage.setItem(key, raw);
      } catch {
        /* private mode - the write just will not survive the session */
      }
      rawCache = raw;
      cache = next;
      onChange?.();
    },
    invalidate() {
      rawCache = null;
    },
  };
}
